/** I9, made structural: the only bridge between an observation and the kernel.
 *
 * `enumerateLegalCommands` is the single legality function (3.3) and it takes a
 * `GameState`. The AI is handed a `PlayerObservation` and must never see more.
 * The resolution is not to give the AI the state and promise it will look away -
 * a promise no test can check - but to *build* a state out of the observation
 * and nothing else. What the fog hid is simply not in the object, so a bug in
 * the planner cannot read it, and this file is the one place to audit.
 *
 * What the shadow contains, and why:
 *
 * - The map planes come straight from `ObservedMap`, which core has already
 *   zeroed outside explored ground. Zero terrain and zero `moveCost` read as
 *   impassable, so the pathfinder declines to route through the dark all by
 *   itself. No extra masking is needed and none is done.
 * - Only the viewer's own heroes and towns are present. An enemy hero is
 *   observed as a position and a level, which cannot be turned into a
 *   `HeroState` without inventing an army; inventing one would be exactly the
 *   leak this file exists to prevent. Enemies live in `Shadow.enemies`, a
 *   separate shape the planner has to reach for deliberately.
 * - Objects carry no payload. The instance payload is hidden by observation, and
 *   the *definition* payload is content, which is public knowledge - so the
 *   planner reads `content.mapObjects` for it rather than fabricating a payload
 *   here that `payloadOf` would then trust.
 * - The RNG streams are seeded from a constant. Nothing in enumeration rolls,
 *   and a shadow that carried the real streams would leak the future.
 */

import {
  CURRENT_FORMAT_VERSION, cellIndex, seedAllStreams,
} from "@game/core";
import type {
  AdventureMapState, ArmyState, CaravanState, ContentRegistry, GameState,
  HeroState, IdKind, MapObjectState, ObservedMap, ObservedObject, PlayerId,
  PlayerObservation, PlayerState, PortalLink, TownState,
} from "@game/core";

/** An enemy asset the observation named, whether lit now or only remembered. */
export interface KnownEnemy {
  readonly kind: "hero" | "town";
  readonly id: string;
  /**
   * Null when the observation cannot say. For a town that is only remembered
   * with no snapshot, and for a lit town that belongs to nobody, this is the
   * same value and deliberately so - the planner treats "not mine" as the fact
   * worth acting on, because it is the fact the observation actually carries.
   */
  readonly owner: PlayerId | null;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly cell: number;
  /** Hero level, or the number of buildings seen in a town. Zero when unknown. */
  readonly rank: number;
  /** Lit right now, as opposed to recalled from a last-seen snapshot. */
  readonly fresh: boolean;
  /**
   * The town's faction, which `observe` gives for any town whose cell has ever
   * been explored - a town is a landmark and its silhouette is its faction.
   * Null for a hero, whose sheet is never visible from the map.
   */
  readonly faction: string | null;
}

export interface Shadow {
  /** A `GameState` whose every field came out of the observation. */
  readonly state: GameState;
  readonly player: PlayerId;
  readonly content: ContentRegistry;
  readonly obs: PlayerObservation;
  /** Objects the observation named, in a stable order. */
  readonly objects: readonly ObservedObject[];
  readonly enemies: readonly KnownEnemy[];
  /**
   * Cells a hero may not step onto even though `moveCost` says they are cheap.
   * `entryIllegality` refuses exactly two things - an obstacle and a locked
   * border gate - and neither is priced into the movement plane, so a path that
   * *starts* on one is the single way this planner can hand the kernel an
   * illegal command. Every path is truncated at the first of these instead.
   */
  readonly blockedCells: ReadonlySet<number>;
  /**
   * Portal jumps this kingdom has taken and can therefore cost a route through.
   * Empty until one of its heroes has come out of a gate; see `portals.ts` for
   * why a gate nobody has been through is not a road the planner may use.
   */
  readonly links: readonly PortalLink[];
}

const ZERO_COUNTERS: Readonly<Record<IdKind, number>> = Object.freeze({
  hero: 0, army: 0, town: 0, object: 0, stack: 0, interaction: 0, caravan: 0,
});

/**
 * The observation's planes are handed straight to the pathfinder rather than
 * copied. Nothing downstream writes to a map plane - the shadow is read-only by
 * construction, and `computeReachable` allocates its own output - so a copy
 * would be a per-command allocation of the whole map for no gain.
 */
function shadowMap(m: ObservedMap): AdventureMapState {
  const n = m.width * m.height * m.levels;
  return {
    width: m.width,
    height: m.height,
    levels: m.levels,
    terrain: m.terrain,
    variant: m.variant,
    road: m.road,
    river: m.river,
    // The planner walks the ground; it does not read what is painted on it.
    ground: m.ground,
    groundIndex: m.groundIndex,
    moveCost: m.moveCost,
    // Object footprints are already priced into `moveCost` by `setOccupant`, so
    // an empty occupancy plane costs the pathfinder nothing. It exists because
    // the shape demands it.
    occupant: new Uint16Array(n),
    objectIndex: [null],
  };
}

function shadowObject(o: ObservedObject): MapObjectState {
  return {
    id: o.id,
    kind: o.kind,
    def: o.def,
    x: o.x, y: o.y, z: o.z,
    owner: o.owner,
    /**
     * Instance state the observation actually reports, and nothing else.
     *
     * `payloadOf` merges a def's payload UNDER an instance's, so an empty
     * payload here does not read as "unknown" - it reads as the def's own
     * starting values. That is what made the planner issue caravans from
     * emptied dwellings (a week's stock off the def) and price a wandering
     * Spearwarden stack as the Imp sprite it was standing on.
     *
     * Zero / omitted when the observation reports none. Failing closed is the
     * right direction for a planner: a column it does not send costs a turn of
     * patience, and one the kernel refuses costs the invariant.
     */
    payload: o.kind === "dwelling" ? { stock: o.stock ?? 0 }
      : o.kind === "monster" && o.creature !== null
        ? { creature: o.creature as string, count: o.creatureCount ?? 0 }
        : {},
    guard: null,
    visitedBy: [],
    removed: false,
  };
}

function num(v: number | string | null | undefined, fallback: number): number {
  return typeof v === "number" ? v : fallback;
}

function str(v: number | string | null | undefined): string | null {
  return typeof v === "string" ? v : null;
}

/**
 * Enemy heroes and towns, from the two channels an observation offers: what is
 * lit right now, and what `remembered` says was there when it was last seen.
 * A remembered entry is deliberately allowed to be wrong - stale knowledge is a
 * feature (3.4), and an AI that refuses to act on it plays worse than a human.
 */
function knownEnemies(obs: PlayerObservation, useMemory: boolean): KnownEnemy[] {
  const out: KnownEnemy[] = [];
  const seen = new Set<string>();
  const cellOf = (x: number, y: number, z: number): number => cellIndex(obs.map, x, y, z);

  for (const h of obs.heroes) {
    // Marked seen whether or not it is ours. The memory pass below must never
    // contradict what the observation is reporting right now; see the towns
    // loop for the bug that cost.
    seen.add(`hero:${h.id}`);
    if (h.owner === obs.viewer) continue;
    out.push({
      kind: "hero", id: h.id, owner: h.owner,
      x: h.x, y: h.y, z: h.z, cell: cellOf(h.x, h.y, h.z),
      rank: h.level, fresh: true, faction: null,
    });
  }
  // Every town the viewer does not own is a target, whether or not it is lit.
  // This is not a fog leak and the leak-shaped alternative is what kept the AI
  // at home: `observe` publishes a town on explored ground on purpose - "a town
  // is a landmark: once its cell is explored the player knows it is there" - and
  // hides only its interior and its current owner. Requiring a *live* owner, as
  // this did, meant the only town the AI would ever march on was one it was
  // already standing next to, so no computer ever went after a capital.
  for (const t of obs.towns) {
    const key = `town:${t.id}`;
    // Seen means "the observation has an opinion about this", not "it is not
    // mine", and the difference was the reason no computer ever finished a war.
    // A town of ours has `detail !== null` and used to be skipped before it was
    // marked - so the *stale snapshot* from before we captured it, which still
    // names the previous owner, fell through to the memory pass below and was
    // published as an enemy capital. Measured on seed 4242 at day 150: `p3`
    // owned `town:6` at (36,3), had two heroes standing in it, and its own
    // planner listed (36,3) as `enemy.town` owned by `p4`. It marched on it 651
    // times in 300 days. Both survivors did this to themselves, which is what
    // "the computers circle each other" was.
    seen.add(key);
    if (t.detail !== null) continue;
    const fresh = obs.map.visibility[cellOf(t.x, t.y, t.z)] === 2;
    const snap = useMemory ? obs.remembered[key] : undefined;
    const owner = fresh ? t.owner : (snap === undefined ? null : str(snap["owner"]) as PlayerId | null);
    out.push({
      kind: "town", id: t.id, owner,
      x: t.x, y: t.y, z: t.z, cell: cellOf(t.x, t.y, t.z),
      rank: snap === undefined ? 0 : num(snap["built"], 0),
      fresh,
      faction: t.faction,
    });
  }

  if (useMemory) {
    for (const key of Object.keys(obs.remembered).sort()) {
      if (seen.has(key)) continue;
      const snap = obs.remembered[key];
      if (snap === undefined) continue;
      const kind = str(snap["kind"]);
      if (kind !== "hero" && kind !== "town") continue;
      const owner = str(snap["owner"]);
      if (owner === null || owner === (obs.viewer as string)) continue;
      const x = num(snap["x"], -1), y = num(snap["y"], -1), z = num(snap["z"], 0);
      if (x < 0 || y < 0) continue;
      out.push({
        kind, id: str(snap["id"]) ?? key, owner: owner as PlayerId,
        x, y, z, cell: cellOf(x, y, z),
        rank: kind === "hero" ? num(snap["level"], 1) : num(snap["built"], 0),
        fresh: false,
        faction: kind === "town" ? str(snap["faction"]) : null,
      });
    }
  }

  out.sort((a, b) => (a.id === b.id ? 0 : a.id < b.id ? -1 : 1));
  return out;
}

/**
 * Build the state the planner is allowed to reason on.
 *
 * Throws only when the observation is not a player's - a spectator has no legal
 * commands to enumerate, so asking for a shadow is a caller bug.
 */
export function buildShadow(
  obs: PlayerObservation, content: ContentRegistry, useMemory = true,
  links: readonly PortalLink[] = [],
): Shadow {
  if (obs.viewer === "spectator" || obs.self === null) {
    throw new Error("buildShadow: a spectator observation has no player to plan for");
  }
  const player = obs.viewer;
  const self: PlayerState = obs.self;

  const heroes: Record<string, HeroState> = {};
  for (const h of obs.heroes) {
    if (h.detail === null) continue;
    heroes[h.id as string] = h.detail;
  }

  const towns: Record<string, TownState> = {};
  for (const t of obs.towns) {
    if (t.detail === null) continue;
    towns[t.id as string] = t.detail;
  }

  const armies: Record<string, ArmyState> = {};
  for (const k of Object.keys(obs.armies).sort()) {
    const a = obs.armies[k];
    if (a !== undefined) armies[k] = a;
  }

  const caravans: Record<string, CaravanState> = {};
  for (const cv of obs.caravans) caravans[cv.id as string] = cv;

  const objects: Record<string, MapObjectState> = {};
  const observed = [...obs.objects].sort((a, b) => (a.id === b.id ? 0 : a.id < b.id ? -1 : 1));
  for (const o of observed) objects[o.id as string] = shadowObject(o);

  const players: Record<string, PlayerState> = {};
  players[player as string] = self;

  const state: GameState = {
    version: obs.version,
    formatVersion: CURRENT_FORMAT_VERSION,
    contentVersion: obs.contentVersion,
    rootSeed: 0,
    rng: seedAllStreams(0),
    counters: { ...ZERO_COUNTERS },
    turn: obs.turn,
    players,
    heroes,
    armies,
    towns,
    objects,
    /**
     * The viewer's own columns, straight from the observation.
     *
     * This used to be empty, with a note saying the observation did not carry
     * caravans and that fixing it belonged in `observe.ts`. It now does, so the
     * planner can count troops that are already walking: without this a town
     * reads as weaker than it will be in two days, and the fix the planner
     * reaches for is to buy the same reinforcements a second time.
     *
     * Keyed by id like every other entity map, so `entityKeys` and the town
     * scorer see the shape they expect.
     */
    caravans,
    map: shadowMap(obs.map),
    fog: { [player as string]: { seen: obs.map.visibility, memory: obs.remembered } },
    battle: obs.battle,
    pending: obs.pending,
    victory: obs.victory,
  };

  return {
    state,
    player,
    content,
    obs,
    objects: observed,
    enemies: knownEnemies(obs, useMemory),
    blockedCells: refusedCells(obs, content, observed),
    links,
  };
}

function refusedCells(
  obs: PlayerObservation, content: ContentRegistry, objects: readonly ObservedObject[],
): ReadonlySet<number> {
  const out = new Set<number>();
  const m = obs.map;
  const mark = (x: number, y: number, z: number): void => {
    if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) return;
    out.add(cellIndex(m, x, y, z));
  };
  for (const o of objects) {
    if (o.kind !== "obstacle" && o.kind !== "gate") continue;
    mark(o.x, o.y, o.z);
    const def = content.mapObjects.get(o.def);
    if (def === undefined) continue;
    for (const [dx, dy] of def.footprint) mark(o.x + dx, o.y + dy, o.z);
  }
  return out;
}
